// src/modules/leads/route-planner/RoutePlannerMobilePanel.js
import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  FaMapMarkerAlt, FaSearch, FaTimes, FaRoute, FaFlag,
  FaCrosshairs, FaChevronUp, FaUndoAlt, FaSearchLocation, FaRedoAlt,
} from 'react-icons/fa';
import GooglePlaceAutocomplete from '../../../components/shared/GooglePlaceAutocomplete';
import LeadSelectionCard from './LeadSelectionCard';
import RouteStopCard from './RouteStopCard';
import FindAlongRoutePanel from './FindAlongRoutePanel';
import RouteSharePanel from './RouteSharePanel';
import {
  BottomSheet, BottomSheetOverlay, DragHandle, SheetSummary,
  SheetSummaryText, SheetContent,
  SectionTitle, PromptCard,
  SearchBox, FilterRow, FilterSelect,
  PrimaryButton, SecondaryButton, ActionButton, ChipButton,
  LeadCountBar, RouteSummaryBar, RouteSummaryStat,
  EmptyState, Spinner, Divider,
  StopCard, StopNumber, StopInfo, StopName, StopAddress,
  OrDivider, ButtonRow, ErrorText,
} from './routePlannerStyles';
import { MARKER_COLORS } from './routeUtils';
import MultiSelectDropdown from '../../../components/shared/MultiSelectDropdown';

const RoutePlannerMobilePanel = ({ planner, onLeadClick }) => {
  const [sheetState, setSheetState] = useState('half');
  const [showAlongRouteSection, setShowAlongRouteSection] = useState(false);

  const handleLeadClick = (lead) => { onLeadClick?.(lead); };
  const touchStartY = useRef(0);
  const touchCurrentY = useRef(0);

  const {
    currentStep,
    departure, departureLoading, departureError, setDeparture, useMyLocation,
    destination, effectiveDestination, isRoundTrip, setDestination, setRoundTrip,
    filteredLeads, mappableLeads, selectedLeadIds,
    leadsLoading, availableCities, availableStatuses, availablePipelineStages, availableServices,
    searchQuery, setSearchQuery,
    cityFilter, setCityFilter,
    statusFilter, setStatusFilter,
    pipelineFilter, setPipelineFilter,
    serviceFilter, setServiceFilter,
    dateRange, setDateRange,
    routeStops, routeInfo, isOptimizing, routeError, routeDirty,
    optimizeRoute, toggleLeadInRoute, removeLeadFromRoute,
    canFindAlongRoute,
    alongRouteDistance, setAlongRouteDistance,
    alongRouteLeads, alongRouteFilters, setAlongRouteFilters,
    showAlongRoute, isSearchingAlongRoute, searchAlongRoute,
    addAlongRouteLeadToRoute,
    googleMapsUrl, copied, copyToClipboard, shareViaWebShare, openInMaps,
    resetRoute, startOver, goToStep, goToShare,
  } = planner;

  // ─── Auto-expand sheet on step transitions ──────────────────────────────
  useEffect(() => {
    // Step 4 (optimized route): start collapsed so user sees the map route
    if (currentStep === 4) {
      setSheetState('collapsed');
    } else {
      setSheetState('half');
    }
  }, [currentStep]);

  // ─── Touch handling (DragHandle + SheetSummary) ─────────────────────────
  const handleTouchStart = useCallback((e) => {
    touchStartY.current = e.touches[0].clientY;
    touchCurrentY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback(() => {
    const diff = touchStartY.current - touchCurrentY.current;
    if (diff > 50) {
      setSheetState(prev => prev === 'collapsed' ? 'half' : 'expanded');
    } else if (diff < -50) {
      setSheetState(prev => prev === 'expanded' ? 'half' : 'collapsed');
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    touchCurrentY.current = e.touches[0].clientY;
  }, []);

  const dragProps = {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
  };

  const getSummaryText = () => {
    switch (currentStep) {
      case 1: return departure ? departure.address.split(',')[0] : 'Set starting point';
      case 2: return 'Set your finish point';
      case 3: return selectedLeadIds.size > 0
        ? `${selectedLeadIds.size} stop${selectedLeadIds.size !== 1 ? 's' : ''} selected`
        : 'Select leads for your route';
      case 4: return routeInfo
        ? `${routeStops.length} stops · ${routeInfo.totalDistance} · ${routeInfo.totalDuration}`
        : 'Route optimized';
      case 5: return 'Share your route';
      default: return '';
    }
  };

  const renderContent = () => {
    switch (currentStep) {
      // ── Step 1: Departure ─────────────────────────────────────────────
      case 1:
        return (
          <PromptCard>
            <FaMapMarkerAlt className="prompt-icon" />
            <h3>{departure ? 'Starting Point Set' : 'Where are you starting?'}</h3>
            {departure ? (
              <p>{departure.address}</p>
            ) : departureError ? (
              <ErrorText>{departureError}</ErrorText>
            ) : null}

            <GooglePlaceAutocomplete
              onSelect={(address, lat, lng) => setDeparture({ lat, lng, address })}
              placeholder="Enter starting address..."
              initialValue={departure?.address || ''}
            />

            <OrDivider><span>or</span></OrDivider>

            <SecondaryButton
              onClick={useMyLocation}
              disabled={departureLoading}
              style={{ width: '100%', justifyContent: 'center', marginTop: '10px' }}
            >
              {departureLoading ? (
                <><Spinner style={{ width: 16, height: 16, borderWidth: 2 }} /> Detecting...</>
              ) : (
                <><FaCrosshairs /> Use My Current Location</>
              )}
            </SecondaryButton>
          </PromptCard>
        );

      // ── Step 2: Destination ───────────────────────────────────────────
      case 2:
        return (
          <div>
            <StopCard>
              <StopNumber color={MARKER_COLORS.departure}>A</StopNumber>
              <StopInfo>
                <StopName>{departure?.address?.split(',')[0]}</StopName>
              </StopInfo>
            </StopCard>

            <SectionTitle><FaFlag /> Finish Point</SectionTitle>

            <ButtonRow>
              <ChipButton active={isRoundTrip} onClick={() => setRoundTrip(true)} style={{ flex: 1, textAlign: 'center' }}>
                <FaUndoAlt style={{ marginRight: '4px' }} /> Round Trip
              </ChipButton>
              <ChipButton active={!isRoundTrip} onClick={() => setRoundTrip(false)} style={{ flex: 1, textAlign: 'center' }}>
                <FaFlag style={{ marginRight: '4px' }} /> Different
              </ChipButton>
            </ButtonRow>

            {!isRoundTrip && (
              <GooglePlaceAutocomplete
                onSelect={(address, lat, lng) => setDestination(address, lat, lng)}
                placeholder="Enter finish address..."
                initialValue={destination?.address || ''}
              />
            )}

            {isRoundTrip && (
              <StopCard>
                <StopNumber color={MARKER_COLORS.destination}>X</StopNumber>
                <StopInfo>
                  <StopName>Same as start</StopName>
                  <StopAddress>{departure?.address}</StopAddress>
                </StopInfo>
              </StopCard>
            )}
          </div>
        );

      // ── Step 3: Select Leads ──────────────────────────────────────────
      case 3:
        return (
          <div>
            {canFindAlongRoute && (
              <div style={{ marginBottom: '10px' }}>
                {!showAlongRouteSection ? (
                  <PrimaryButton
                    onClick={() => setShowAlongRouteSection(true)}
                    style={{ background: '#9b59b6' }}
                  >
                    <FaSearchLocation /> Find Leads Along Route
                  </PrimaryButton>
                ) : (
                  <FindAlongRoutePanel
                    alongRouteDistance={alongRouteDistance}
                    setAlongRouteDistance={setAlongRouteDistance}
                    alongRouteLeads={alongRouteLeads}
                    alongRouteFilters={alongRouteFilters}
                    setAlongRouteFilters={setAlongRouteFilters}
                    showAlongRoute={showAlongRoute}
                    isSearching={isSearchingAlongRoute}
                    onSearch={searchAlongRoute}
                    onAddLead={addAlongRouteLeadToRoute}
                    onLeadClick={handleLeadClick}
                    availableStatuses={availableStatuses}
                  />
                )}
                <Divider />
              </div>
            )}

            <SectionTitle><FaSearch /> Browse Leads</SectionTitle>

            <SearchBox>
              <FaSearch />
              <input
                type="text"
                placeholder="Search leads..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && <FaTimes style={{ cursor: 'pointer' }} onClick={() => setSearchQuery('')} />}
            </SearchBox>

            <FilterRow>
              <FilterSelect value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
                <option value="all">All Time</option>
                <option value="7">7 Days</option>
                <option value="14">14 Days</option>
                <option value="30">30 Days</option>
                <option value="60">60 Days</option>
                <option value="90">90 Days</option>
              </FilterSelect>
              <FilterSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">All Statuses</option>
                {availableStatuses.map(s => <option key={s} value={s}>{s}</option>)}
              </FilterSelect>
              <FilterSelect value={cityFilter} onChange={(e) => setCityFilter(e.target.value)}>
                <option value="all">All Cities</option>
                {availableCities.map(c => <option key={c} value={c}>{c}</option>)}
              </FilterSelect>
            </FilterRow>

            <FilterRow>
              <FilterSelect value={pipelineFilter} onChange={(e) => setPipelineFilter(e.target.value)}>
                <option value="all">All Pipeline Stages</option>
                {availablePipelineStages.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </FilterSelect>
            </FilterRow>

            {availableServices.length > 0 && (
              <FilterRow style={{ marginTop: '8px' }}>
                <MultiSelectDropdown
                  options={availableServices}
                  selected={serviceFilter}
                  onChange={setServiceFilter}
                  placeholder="All Services"
                />
              </FilterRow>
            )}

            {selectedLeadIds.size > 0 && (
              <PrimaryButton
                onClick={optimizeRoute}
                disabled={isOptimizing}
                style={{ marginBottom: '10px' }}
              >
                <FaRoute />
                {isOptimizing ? 'Optimizing...' : `Optimize (${selectedLeadIds.size} stops)`}
              </PrimaryButton>
            )}

            {routeError && <ErrorText>{routeError}</ErrorText>}

            <LeadCountBar>
              {filteredLeads.length} leads · {mappableLeads.length} mappable
              {selectedLeadIds.size > 0 && ` · ${selectedLeadIds.size} selected`}
            </LeadCountBar>

            {leadsLoading ? (
              <EmptyState><Spinner /></EmptyState>
            ) : filteredLeads.length === 0 ? (
              <EmptyState>No leads found</EmptyState>
            ) : (
              filteredLeads.map(lead => (
                <LeadSelectionCard
                  key={lead.id}
                  lead={lead}
                  isSelected={selectedLeadIds.has(lead.id)}
                  onToggle={toggleLeadInRoute}
                  onClick={handleLeadClick}
                />
              ))
            )}
          </div>
        );

      // ── Step 4: Route Optimized ───────────────────────────────────────
      case 4:
        return (
          <div>
            {routeInfo && (
              <RouteSummaryBar>
                <RouteSummaryStat>
                  <div className="value">{routeStops.length}</div>
                  <div className="label">Stops</div>
                </RouteSummaryStat>
                <RouteSummaryStat>
                  <div className="value">{routeInfo.totalDistance}</div>
                  <div className="label">Distance</div>
                </RouteSummaryStat>
                <RouteSummaryStat>
                  <div className="value">{routeInfo.totalDuration}</div>
                  <div className="label">Time</div>
                </RouteSummaryStat>
              </RouteSummaryBar>
            )}

            <ButtonRow>
              <SecondaryButton onClick={() => goToStep(3)} style={{ flex: 1 }}>
                Edit Stops
              </SecondaryButton>
              <PrimaryButton onClick={goToShare} style={{ flex: 1 }} disabled={routeDirty}>
                Share Route
              </PrimaryButton>
            </ButtonRow>

            {routeDirty && (
              <PrimaryButton onClick={optimizeRoute} disabled={isOptimizing} style={{ marginBottom: '12px' }}>
                <FaRoute />
                {isOptimizing ? 'Recalculating...' : `Recalculate (${selectedLeadIds.size} stops)`}
              </PrimaryButton>
            )}

            <StopCard>
              <StopNumber color={MARKER_COLORS.departure}>A</StopNumber>
              <StopInfo><StopName>Start</StopName><StopAddress>{departure?.address}</StopAddress></StopInfo>
            </StopCard>

            {routeStops.map((lead, i) => (
              <RouteStopCard key={lead.id} lead={lead} index={i} legInfo={routeInfo?.legs?.[i]} onRemove={removeLeadFromRoute} />
            ))}

            <StopCard>
              <StopNumber color={isRoundTrip ? MARKER_COLORS.departure : MARKER_COLORS.destination}>
                {isRoundTrip ? 'A' : 'X'}
              </StopNumber>
              <StopInfo>
                <StopName>{isRoundTrip ? 'Return to Start' : 'Finish'}</StopName>
                <StopAddress>{effectiveDestination?.address}</StopAddress>
              </StopInfo>
            </StopCard>

            {canFindAlongRoute && (
              <FindAlongRoutePanel
                alongRouteDistance={alongRouteDistance}
                setAlongRouteDistance={setAlongRouteDistance}
                alongRouteLeads={alongRouteLeads}
                alongRouteFilters={alongRouteFilters}
                setAlongRouteFilters={setAlongRouteFilters}
                showAlongRoute={showAlongRoute}
                isSearching={isSearchingAlongRoute}
                onSearch={searchAlongRoute}
                onAddLead={addAlongRouteLeadToRoute}
                onLeadClick={handleLeadClick}
                availableStatuses={availableStatuses}
              />
            )}
          </div>
        );

      // ── Step 5: Share ─────────────────────────────────────────────────
      case 5:
        return (
          <RouteSharePanel
            routeInfo={routeInfo}
            routeStops={routeStops}
            googleMapsUrl={googleMapsUrl}
            copied={copied}
            onCopy={copyToClipboard}
            onOpenInMaps={openInMaps}
            onShare={shareViaWebShare}
            onReset={resetRoute}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      <BottomSheetOverlay
        expanded={sheetState === 'expanded'}
        onClick={() => setSheetState('half')}
      />
      <BottomSheet sheetState={sheetState}>
        {/* DragHandle + Summary both draggable */}
        <DragHandle
          {...dragProps}
          onClick={() => setSheetState(prev =>
            prev === 'collapsed' ? 'half' : prev === 'half' ? 'expanded' : 'half'
          )}
        />
        <SheetSummary {...dragProps}>
          <SheetSummaryText>{getSummaryText()}</SheetSummaryText>
          <ActionButton
            variant="primary"
            onClick={() => setSheetState(prev =>
              prev === 'expanded' ? 'half' : 'expanded'
            )}
          >
            <FaChevronUp style={{
              transform: sheetState === 'expanded' ? 'rotate(180deg)' : 'rotate(0)',
              transition: 'transform 0.2s',
            }} />
          </ActionButton>
        </SheetSummary>
        <SheetContent>
          {renderContent()}
          {currentStep > 1 && (
            <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: '1px solid #78787833' }}>
              <SecondaryButton onClick={startOver} style={{ width: '100%', justifyContent: 'center' }}>
                <FaRedoAlt /> Start Over
              </SecondaryButton>
            </div>
          )}
        </SheetContent>
      </BottomSheet>
    </>
  );
};

export default RoutePlannerMobilePanel;
